if (!customElements.get('tier-pricing')) {
    customElements.define('tier-pricing', class TierPricing extends HTMLElement {
        constructor() {
            super();
            this.variantId = this.dataset.variantId;
            this.currentQuantity = 0;
            this.variantMetafields = JSON.parse(this.dataset.variantMetafields || '[]');
            this.mainProductQuantityBreaks = this.dataset.quantityBreaks || '';

            console.log(`Initializing Tier Pricing for variant ID:`, this.productQuantityBreaks);
            // Store product-level data
            this.productBasePrice = parseFloat(this.dataset.basePrice) || 0;
            this.productPricingTiers = (this.dataset.pricingTiers || '').split(',')
                .map(price => parseFloat(price))
                .filter(price => !isNaN(price));
            this.productQuantityBreaks = (this.dataset.quantityBreaks || '').split(',')
                .map(qty => qty.trim() === '*' ? '*' : parseInt(qty))
                .filter(qty => qty === '*' || !isNaN(qty));
            this.productMinimumQuantity = parseInt(this.dataset.minimumQuantity) || 1;

            console.log(`Initializing Tier Pricing for variant ID:`, this.mainProductQuantityBreaks);

            this.tableElement = this.querySelector('.tier-pricing__table');
            this.savingsElement = this.querySelector('.tier-pricing__savings');

            // Initialize with appropriate data
            this.updateVariantData();
        }

        connectedCallback() {
            this.init();
        }

        init() {
            this.setupVariantListener();
            this.toggleVisibility();
            this.renderTable();
            this.setupCartListener();
            this.updateCurrentTier();
        }

        setupVariantListener() {
            document.addEventListener('variant:changed', (event) => {
                const newVariantId = event.detail.variant.id.toString();
                if (this.variantId !== newVariantId) {
                    this.variantId = newVariantId;
                    this.updateVariantData();
                    this.toggleVisibility();
                    this.renderTable();
                    this.updateCurrentTier();
                }
            });
        }

        updateVariantData() {
            // Check if we're dealing with a variant selection scenario
            const isVariantSelected = this.variantId && this.variantMetafields.length > 0;

            // Check if product-level pricing tiers exist and are valid
            const hasProductPricing = this.productPricingTiers.length > 0 &&
                this.productQuantityBreaks.length > 0 &&
                this.productPricingTiers.some(price => price > 0);

            if (isVariantSelected) {
                const currentVariant = this.variantMetafields.find(v => v.id.toString() === this.variantId.toString());
                console.log(`Current variant ID: ${this.variantId}`, this.mainProductQuantityBreaks, this.mainProductQuantityBreaks);
                // Check if selected variant has its own pricing data
                if (currentVariant && currentVariant.pricing_tiers &&
                    currentVariant.pricing_tiers.trim() !== '') {

                    // Use variant-specific pricing; fall back to product-level quantity breaks if variant's are missing
                    this.basePrice = parseFloat(currentVariant.price) || 0;
                    this.pricingTiers = (currentVariant.pricing_tiers || '').split(',')
                        .map(price => parseFloat(price))
                        .filter(price => !isNaN(price));
                    const qbSource = (currentVariant.quantity_breaks && currentVariant.quantity_breaks.trim() !== '')
                        ? currentVariant.quantity_breaks
                        : this.mainProductQuantityBreaks;
                    this.quantityBreaks = (qbSource || '').split(',')
                        .map(qty => qty.trim() === '*' ? '*' : parseInt(qty))
                        .filter(qty => qty === '*' || !isNaN(qty));
                    this.minimumQuantity = parseInt(currentVariant.minimum_quantity) || 1;
                    this.useProductPricing = false;
                }
                // Special case: This specific variant ID should show product pricing
                else if (currentVariant &&
                    currentVariant.id.toString() === "50562527559980" &&
                    hasProductPricing) {
                    // Use product pricing for this specific variant
                    this.basePrice = parseFloat(currentVariant.price) || this.productBasePrice;
                    this.pricingTiers = this.productPricingTiers;
                    this.quantityBreaks = this.productQuantityBreaks;
                    this.minimumQuantity = this.productMinimumQuantity;
                    this.useProductPricing = true;
                }
                else {
                    // Other variants without pricing tiers - hide table
                    this.basePrice = currentVariant ? parseFloat(currentVariant.price) || 0 : 0;
                    this.pricingTiers = [];
                    this.quantityBreaks = [];
                    this.minimumQuantity = 1;
                    this.useProductPricing = false;
                }
            } else {
                // No variant is selected, or product doesn't have variants
                // Use product-level pricing if available
                this.basePrice = this.productBasePrice;
                this.pricingTiers = this.productPricingTiers;
                this.quantityBreaks = this.productQuantityBreaks;
                this.minimumQuantity = this.productMinimumQuantity;
                this.useProductPricing = true;
            }
        }

        toggleVisibility() {
            // Show/hide the entire component based on whether we have valid pricing data
            if (this.hasValidPricingData()) {
                this.style.display = '';
            } else {
                this.style.display = 'none';
            }
        }

        hasValidPricingData() {
            return this.pricingTiers?.length > 0 &&
                this.quantityBreaks?.length > 0 &&
                this.pricingTiers.some(price => price > 0);
        }

        renderTable() {
            if (!this.hasValidPricingData()) {
                this.tableElement.innerHTML = '';
                return;
            }

            const table = document.createElement('table');
            table.className = 'tier-pricing__table';

            // Add header
            const header = `
                <thead>
                    <tr class="tier-pricing__row">
                        <th class="tier-pricing__cell">Quantity</th>
                        <th class="tier-pricing__cell">Price per unit</th>
                    </tr>
                </thead>
            `;

            let rows = '';

            // Generate table rows
            for (let i = 0; i < this.quantityBreaks.length; i++) {
                const currentBreak = this.quantityBreaks[i];
                const nextBreak = this.quantityBreaks[i + 1];
                const price = this.pricingTiers[i];

                let rangeText;
                if (i === 0) {
                    if (currentBreak != '1'){
                        rangeText = `${this.minimumQuantity}-${currentBreak}`;
                    } else {
                        rangeText = `${currentBreak}`
                    }
                } else if (currentBreak === '*' || i === this.quantityBreaks.length - 1) {
                    const prevBreak = this.quantityBreaks[i - 1];
                    rangeText = `${prevBreak + 1}+`;
                } else {
                    const prevBreak = this.quantityBreaks[i - 1];
                    rangeText = `${prevBreak + 1}-${currentBreak}`;
                }

                rows += `
                    <tr class="tier-pricing__row" data-tier="${i}">
                        <td class="tier-pricing__cell">${rangeText}</td>
                        <td class="tier-pricing__cell">$${price.toFixed(2)}</td>
                    </tr>
                `;

                if (currentBreak === '*') break;
            }

            table.innerHTML = `${header}<tbody>${rows}</tbody>`;
            this.tableElement.innerHTML = '';
            this.tableElement.appendChild(table);
        }

        calculateTierPrice(quantity) {
            if (!this.hasValidPricingData()) return this.basePrice;

            let tierPrice = this.pricingTiers[0]; // Default to first tier price

            for (let i = 0; i < this.quantityBreaks.length; i++) {
                const break_ = this.quantityBreaks[i];

                if (break_ === '*') {
                    // If we hit the "*" tier, use its price for all higher quantities
                    if (quantity > this.quantityBreaks[i - 1]) {
                        return this.pricingTiers[i];
                    }
                    break;
                }

                if (quantity > break_) {
                    tierPrice = this.pricingTiers[i + 1];
                }
            }

            return tierPrice;
        }

        updateCurrentTier() {
            if (!this.hasValidPricingData()) {
                this.savingsElement.classList.remove('tier-pricing__savings--visible');
                return;
            }

            const tierPrice = this.calculateTierPrice(this.currentQuantity);
            const savings = ((this.basePrice - tierPrice) * this.currentQuantity).toFixed(2);

            // Update active row
            this.querySelectorAll('.tier-pricing__row').forEach(row => {
                row.classList.remove('tier-pricing__row--active');
            });

            // Find and highlight active tier
            for (let i = 0; i < this.quantityBreaks.length; i++) {
                const break_ = this.quantityBreaks[i];
                const nextBreak = this.quantityBreaks[i + 1];

                if (
                    break_ === '*' && this.currentQuantity > this.quantityBreaks[i - 1] ||
                    this.currentQuantity > (i > 0 ? this.quantityBreaks[i - 1] : 0) &&
                    (!nextBreak || this.currentQuantity <= break_)
                ) {
                    const row = this.querySelector(`[data-tier="${i}"]`);
                    if (row) row.classList.add('tier-pricing__row--active');
                    break;
                }
            }

            // Update savings message
            if (savings > 0) {
                this.savingsElement.textContent = `Your volume discount saves you $${savings} on this order!`;
                this.savingsElement.classList.add('tier-pricing__savings--visible');
            } else {
                this.savingsElement.classList.remove('tier-pricing__savings--visible');
            }
        }

        setupCartListener() {
            // Listen for cart changes
            document.addEventListener('cart:updated', (event) => {
                const cartItems = event.detail?.items || [];
                this.currentQuantity = cartItems.reduce((total, item) => {
                    if (item.variant_id === this.variantId) {
                        return total + item.quantity;
                    }
                    return total;
                }, 0);

                this.updateCurrentTier();
            });

            // Listen for quantity input changes
            const quantityInput = document.querySelector('input[name="quantity"]');
            if (quantityInput) {
                quantityInput.addEventListener('change', (event) => {
                    this.currentQuantity = parseInt(event.target.value) || 0;
                    this.updateCurrentTier();
                });
            }

            if (window.FoxThemeEvents?.subscribe) {
                window.FoxThemeEvents.subscribe('quantity-update', () => {
                    const input = document.querySelector('input[name="quantity"]');
                    if (input) {
                        this.currentQuantity = parseInt(input.value) || 0;
                        this.updateCurrentTier();
                    }
                });
            }
        }
    });
}
